import type { LevelDef } from './types';

/**
 * Day 5 —Saturday, fog. The hill road: switchbacks, a steep stairway, the
 * shrine steps. She can barely see, so the phone map and sound matter. He
 * appears in the convex traffic mirrors. An unknown number keeps calling.
 */
export const night4: LevelDef = {
  id: 'night4',
  night: 5,
  title: 'Hill Fog',
  subtitle: 'Saturday · 00:12 · fog',
  look: 'casual',
  card: ['Fog rolled in off the bay.', 'Your street is up the hill, past the shrine.', 'An unknown number has called you three times today.'],
  gps: 'weak',
  outfit: 'weekend',
  wind: 0.08,
  clockStart: 12,
  map: [
    '#########################', // 0
    '##############H##########', // 1
    '##############.##########', // 2
    '##############.##########', // 3
    '######....B....##########', // 4
    '######.#############.####', // 5
    '######.#############.A###', // 6
    '######L#############L####', // 7
    '######.#############.####', // 8
    '######..........T....#.##', // 9
    '############.#########.##', // 10
    '############.#########L##', // 11
    '############F#########.##', // 12
    '############.#########.##', // 13
    '############.#########.##', // 14
    '####pppppp..L..........##', // 15
    '####p###############.####', // 16
    '####p###############V####', // 17
    '####p###############.####', // 18
    '####p###############.####', // 19
    '####p###############.####', // 20
    '####p###############L####', // 21
    '####p###############.####', // 22
    '####p###############.####', // 23
    '####G###############.####', // 24
    '####....L...z...V....####', // 25
    '####.#######z#######.####', // 26
    '####.#######z#######.####', // 27
    '####.#######z#######.####', // 28
    '####.#######z#######.####', // 29
    '####V#######z#######L####', // 30
    '####.#######z#######.####', // 31
    '####.#######z#######.####', // 32
    '####.#######z#######.####', // 33
    '####.#######z#######.####', // 34
    '####........V........####', // 35
    '####.###############.####', // 36
    '####.###############.####', // 37
    '####.###############.####', // 38
    '####F###############L####', // 39
    '####.###############.####', // 40
    '####.###############.####', // 41
    '####.###############.####', // 42
    '####...V........L....####', // 43
    '############.############', // 44
    '############V############', // 45
    '############.############', // 46
    '############.############', // 47
    '############.############', // 48
    '############S############', // 49
    '############.############', // 50
    '############P############', // 51
  ],
  zones: [
    { r0: 36, zone: 1 },
    { r0: 16, r1: 35, zone: 2 },
    { r1: 15, zone: 3 },
  ],
  battery: 24,
  pace: { tension: 0.6, patience: 760 },
  mirrorSightings: true,
  theme: {
    sky: '#1b2027',
    fog: { color: '#262c34', density: 0.085 },
    hemi: { sky: '#6a7a90', ground: '#14161a', intensity: 0.85 },
    ambient: 0.6,
    moon: 0.2,
    overcast: true,
  },
  stranger: { walkMax: 2.95, chaseSpeed: 3.1, gapMax: 33, spawnDelay: 5 },
  lines: {
    intro: 'I can\'t see the end of the street.',
    firstSight: 'A shape in the fog… walking when I walk.',
    mirror: 'The mirror… he\'s right there, around the corner.',
    stairs: 'The steps. Steep, no lights… but straight up.',
    path: 'The shrine steps. Even the fog is quieter here.',
    pathWait: 'He won\'t come up the shrine steps.',
    pathExitGone: 'Out of the trees. I can\'t see him. That\'s worse.',
    deadend: 'Dead end. The fog made it look like it kept going.',
    call: 'Unknown number again. …Nobody\'s there. Just breathing.',
    wrongBuilding: 'Not this one. Mine is further along.',
    homeStretch: 'My street. Finally.',
  },
  mom: {
    first: { node: 'start', at: 6 },
    script: {
      start: {
        text: 'Fog warning on the news. Are you walking?',
        options: [
          { text: 'Yes, almost home', end: 'Slowly. Watch the steps.' },
          { text: 'Someone keeps calling me', next: 'calls' },
        ],
      },
      calls: {
        text: "Who? Don't answer unknown numbers!",
        options: [
          { text: "I didn't", end: 'Good.' },
          { text: "I think it's him", next: 'him' },
        ],
      },
      him: {
        text: 'Then block it. And come home. NOW.',
        options: [
          { text: 'Ok', end: "I'm watching the door." },
          { text: "I'm scared", end: "I know. Keep walking. I'm here." },
        ],
      },
      fog: {
        text: "Can you even see where you're going?",
        options: [
          { text: 'Barely', end: 'Use the map on your phone.' },
          { text: "There's someone behind me", next: 'behind' },
        ],
      },
      behind: {
        text: 'Take the shrine steps. The priest lives up there.',
        options: [
          { text: "It's so dark there", end: 'Dark is fine. Go.' },
          { text: 'Ok', end: 'Go.' },
        ],
      },
    },
  },
  onEnterCell(ctx, cell) {
    const key = `${cell.c},${cell.r}`;
    if (key === '12,34') ctx.sayOnce('stairs');
    if (key === '22,9' && !ctx.said('deadend')) {
      ctx.spike(0.06);
      ctx.sayOnce('deadend');
    }
    if (cell.r === 4) ctx.sayOnce('homeStretch');
  },
  onZoneChange(ctx, zone) {
    if (zone === 2 && !ctx.said('call')) {
      ctx.ring();
      ctx.notify('call4', 'Unknown', 'Missed call', 'system', 3);
      ctx.sayLater('call', 3.5);
      ctx.momLater('fog', 9);
    }
  },
};
